import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  TabContent,
  Table,
  TabPane,
  Spinner,
  CardFooter,
} from 'reactstrap';
import './TicketListing.scss';
import {
  OPEN_TICKETS_HEADERS,
  RESOLVED_TICKETS_HEADERS,
  TICKET_STATUS_BADGE_STYLES,
  TICKET_MAIN_STATUS,
} from './constants';
import CreateTicket from '../../../components/Ticket/CreateTicket/CreateTicket';
import { fetchTicketDetailsListing } from '../../../store/apis/ticketService';
import useTableHook from '../../../components/hooks/useTableHook';
import {
  FilterDropdown,
  TableSortDisplayArrow,
  Badge,
  TableFilterBubbles,
  DatePicker,
  TablePagination,
} from '../../../components/Common';
import { getAuthUser } from '../../../utils/auth.util';
import { fetchTableColumnValues } from '../../../store/apis/commonService';

const getActiveTabFromQuery = queryTab => {
  if (queryTab === '1') {
    return '1';
  }
  if (queryTab === '2') {
    return '2';
  }
  return '1';
};

const initialPageRequestTicketConfig = queryTab => {
  if (queryTab === '1') {
    return {
      ticketStatus: TICKET_MAIN_STATUS.OPEN,
    };
  }
  if (queryTab === '2') {
    return {
      ticketStatus: TICKET_MAIN_STATUS.CLOSED,
    };
  }
  return {
    ticketStatus: TICKET_MAIN_STATUS.OPEN,
  };
};

function TicketListing() {
  document.title = 'Tickets | Envision Daikin';
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    getActiveTabFromQuery(queryParams.get('t')) || '1',
  );
  const [createTicketOffcanvas, setCreateTicketOffcanvas] = useState(false);
  const [tableHeaders, setTableHeaders] = useState(OPEN_TICKETS_HEADERS);

  // Table hook for managing ticket data
  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleManualSort,
    checkColumnIfSorted,
    applyExternalFilters,
    isLoadingData,
    isLoadingColumn,
    clearSingleFilterValue,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'TicketNo',
      sortDirection: 'DESC',
      filters: [],
      requestor: getAuthUser()?.employeeId,
      ...initialPageRequestTicketConfig(queryParams.get('t')),
    },
    fetchFn: fetchTicketDetailsListing,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'ticketListing',
    columnKeyPrefix: 'ticketListingColumn',
    tableName: 'TicketListing',
    defaultExternalFilters: {},
    tableQueryOptions: {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    },
  });

  // Effect to set the table headers based on the active tab
  useEffect(() => {
    if (activeTab === '1') {
      setTableHeaders(OPEN_TICKETS_HEADERS);
    } else if (activeTab === '2') {
      setTableHeaders(RESOLVED_TICKETS_HEADERS);
    }
  }, [activeTab]);

  // Function to toggle the create ticket offcanvas
  const toggleCreateTicketOffcanvas = () => {
    setCreateTicketOffcanvas(!createTicketOffcanvas);
  };

  // Function to toggle the active tab
  const toggleTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      resetFilters();
    }
  };

  const generateSinceDays = item => {
    if (item?.OpenSinceDays === 0) {
      return 'Today';
    }
    if (item?.OpenSinceDays === 1) {
      return '1 day';
    }
    return `${item?.OpenSinceDays} days`;
  };

  const getNoTicketMessage = () => {
    if (activeTab === '1') {
      return 'No Open Tickets';
    }

    if (activeTab === '2') {
      return 'No Closed Tickets';
    }

    return 'No Tickets';
  };

  // Render the table rows for tickets based on the active tab
  const renderTickets = () => {
    const colSpanCount = Object.entries(tableHeaders).length;
    const tableData = data?.data || [];

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={colSpanCount}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              getNoTicketMessage()
            )}
          </td>
        </tr>
      );
    }

    if (activeTab === '1') {
      return tableData?.map(item => (
        <tr
          className="cursor-pointer"
          key={item?.TicketNo}
          onClick={() => navigate(`/tickets/${item.TicketNo}?fr=${activeTab}`)}
        >
          <td>
            <span
              onClick={() =>
                navigate(`/tickets/${item.TicketNo}?fr=${activeTab}`)
              }
              className="text-decoration-underline p-0 px-3 cursor-pointer ticket-no"
            >
              {item?.TicketNo}
            </span>
          </td>
          <td className="px-4">{item?.Subject}</td>
          <td className="px-4">{item?.Category}</td>
          <td className="px-4">{item?.CreatedOn}</td>
          <td className="px-4">
            <Badge
              config={TICKET_STATUS_BADGE_STYLES[item.Status]}
              labelType="TitleCase"
              badgeType="pill"
            >
              {item.Status}
            </Badge>
          </td>
          <td className="px-4">{generateSinceDays(item)}</td>
        </tr>
      ));
    }

    if (activeTab === '2') {
      return tableData?.map(item => (
        <tr
          className="cursor-pointer"
          key={item?.TicketNo}
          onClick={() => navigate(`/tickets/${item.TicketNo}?fr=${activeTab}`)}
        >
          <td>
            <span
              onClick={() =>
                navigate(`/tickets/${item.TicketNo}?fr=${activeTab}`)
              }
              className="text-decoration-underline p-0 px-3 cursor-pointer ticket-no"
            >
              {item?.TicketNo}
            </span>
          </td>
          <td className="px-4">{item?.Subject}</td>
          <td className="px-4">{item?.Category}</td>
          <td className="px-4">{item?.CreatedOn}</td>
          <td className="px-4">
            <Badge
              config={TICKET_STATUS_BADGE_STYLES[item.Status]}
              labelType="TitleCase"
              badgeType="pill"
            >
              {item.Status}
            </Badge>
          </td>
          <td className="px-4">{item?.StatusLastDate}</td>
        </tr>
      ));
    }
    return null;
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.ticketFromDate &&
      externalFilters.ticketToDate &&
      new Date(externalFilters?.ticketFromDate) >
        new Date(externalFilters?.ticketToDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content ticket-listing-page">
      <Card className="ticket-card">
        <CardHeader className="p-3 pb-2">
          <div className="d-flex flex-row justify-content-between align-items-center mb-2">
            <div className="fw-semibold fs-5">Tickets</div>
            <div>
              <button
                type="button"
                className="custom-btn"
                onClick={toggleCreateTicketOffcanvas}
              >
                New Ticket
              </button>
            </div>
          </div>
          <div className="ticket-external-filter d-flex flex-row align-items-end justify-content-start gap-2">
            <Col lg={3} className="external-filter-col">
              <b>Search</b>
              <div className="search-box-right">
                <Input
                  type="text"
                  placeholder="Search Tickets"
                  onChange={e => {
                    setExternalFilters(prev => ({
                      ...prev,
                      ticketNo: e.target.value,
                    }));
                  }}
                  value={externalFilters?.ticketNo || ''}
                  className="external-filter-input"
                />
                <span className="search-icon ri-search-line" />
              </div>
            </Col>
            <Col lg={3} className="external-filter-col">
              <b>From Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    ticketFromDate: e,
                  }))
                }
                value={externalFilters?.ticketFromDate || ''}
                className="form-control external-filter-input"
              />
            </Col>
            <Col lg={3} className="external-filter-col">
              <b>To Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    ticketToDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.ticketToDate || ''}
              />
            </Col>
            <Col className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="custom-btn"
                onClick={() => {
                  if (!externalFiltersValidation()) {
                    return;
                  }
                  applyExternalFilters(externalFilters);
                }}
              >
                Search
              </button>
            </Col>
          </div>
          <div className="mt-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              clearAllFilters={resetFilters}
            />
          </div>
        </CardHeader>
        <CardBody className="p-0">
          <div className="d-flex flex-row justify-content-between align-items-center ticket-header">
            <ul className="nav nav-underline">
              <button
                type="button"
                className={`nav-link px-3 cursor-pointer ${activeTab === '1' && 'active'}`}
                onClick={() => {
                  toggleTab('1');
                  setPageRequest(prev => ({
                    ...prev,
                    ticketStatus: TICKET_MAIN_STATUS.OPEN,
                  }));
                }}
                title="Pending tickets created by you"
                style={{
                  backgroundColor:
                    activeTab === '1' ? '#F0F9FF' : 'transparent',
                }}
              >
                Open
              </button>
              <button
                type="button"
                className={`nav-link px-3 cursor-pointer ${activeTab === '2' && 'active'}`}
                onClick={() => {
                  toggleTab('2');
                  setPageRequest(prev => ({
                    ...prev,
                    ticketStatus: TICKET_MAIN_STATUS.CLOSED,
                  }));
                }}
                title="Tickets that have been closed"
                style={{
                  backgroundColor:
                    activeTab === '2' ? '#F0F9FF' : 'transparent',
                }}
              >
                Closed
              </button>
            </ul>
          </div>

          <div className="ticket-body-separator" />
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1" className="p-0">
              <div className="table-responsive ticket-table-container">
                <Table
                  hover
                  className="custom-table m-0 ticket-table"
                  id="tickets-enquiry"
                >
                  <thead>
                    <tr>
                      {Object.entries(tableHeaders).map(([key, label]) => (
                        <th
                          className="px-4"
                          key={key}
                          style={{ width: label === 'Subject' ? '40%' : '' }}
                        >
                          <div className="d-flex flex-row align-items-center justify-content-start gap-1">
                            <div
                              className="p-0 cursor-pointer"
                              onClick={() => handleAutoSort(key)}
                            >
                              {label}
                            </div>
                            <TableSortDisplayArrow
                              direction={checkColumnIfSorted(key)}
                            />
                            <FilterDropdown
                              handleSetColumnName={() => {
                                setColumnName(key);
                              }}
                              setColumnName={setColumnName}
                              columnData={columnData}
                              applyFilter={applyFilter}
                              columnName={columnName}
                              pageRequest={pageRequest}
                              setPageRequest={setPageRequest}
                              handleManualSort={handleManualSort}
                              isLoading={isLoadingColumn}
                            />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{renderTickets()}</tbody>
                </Table>
              </div>
            </TabPane>
            <TabPane tabId="2" className="p-0">
              <div className="table-responsive ticket-table-container">
                <Table
                  hover
                  className="custom-table m-0 ticket-table"
                  id="tickets-enquiry"
                >
                  <thead>
                    <tr>
                      {Object.entries(tableHeaders).map(([key, label]) => (
                        <th
                          className="px-4"
                          key={key}
                          style={{ width: label === 'Subject' ? '40%' : '' }}
                        >
                          <div className="d-flex flex-row align-items-center justify-content-start gap-1">
                            <div
                              className="p-0 cursor-pointer"
                              onClick={() => handleAutoSort(key)}
                            >
                              {label}
                            </div>
                            <TableSortDisplayArrow
                              direction={checkColumnIfSorted(key)}
                            />
                            <FilterDropdown
                              handleSetColumnName={() => {
                                setColumnName(key);
                              }}
                              setColumnName={setColumnName}
                              columnData={columnData}
                              applyFilter={applyFilter}
                              columnName={columnName}
                              pageRequest={pageRequest}
                              setPageRequest={setPageRequest}
                              handleManualSort={handleManualSort}
                            />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>{renderTickets()}</tbody>
                </Table>
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
      <CreateTicket
        isOpen={createTicketOffcanvas}
        toggle={toggleCreateTicketOffcanvas}
      />
    </div>
  );
}

export default TicketListing;
